import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { CreateTemplateDialog } from "./components/create-template-dialog";
import { TemplateCard } from "./components/template-card";
import { useServiceTemplates } from "./hooks/use-service-templates";

export function ServiceTemplatesSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation("settings");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    systemTemplates,
    customTemplates,
    loading,
    error,
    creating,
    deleting,
    createTemplate,
    deleteTemplate,
  } = useServiceTemplates();

  return (
    <>
      <BaseNavigationBar
        title={t("serviceTemplates.title")}
        subtitle={t("serviceTemplates.description")}
        leadElement={
          isMobile ? (
            <BackButton key="back-button" onBack={() => navigate("/salon/settings")} />
          ) : undefined
        }
        trailingElements={[
          <Button key="create" onClick={() => setCreateDialogOpen(true)} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            {t("serviceTemplates.actions.create")}
          </Button>,
        ]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={error} />

          {!error && (
            <>
              {/* System Templates Section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">{t("serviceTemplates.sections.system")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("serviceTemplates.sections.systemDescription")}
                  </p>
                </div>

                {loading ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-48" />
                    ))}
                  </div>
                ) : systemTemplates.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {systemTemplates.map((template) => (
                      <TemplateCard key={template.id} template={template} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t("serviceTemplates.messages.noSystemTemplates")}
                    </p>
                  </div>
                )}
              </div>

              {/* Custom Templates Section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">{t("serviceTemplates.sections.custom")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("serviceTemplates.sections.customDescription")}
                  </p>
                </div>

                {loading ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-48" />
                    ))}
                  </div>
                ) : customTemplates.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {customTemplates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onDelete={deleteTemplate}
                        deleting={deleting}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-8 text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("serviceTemplates.messages.noCustomTemplates")}
                    </p>
                    <Button onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t("serviceTemplates.actions.createFirst")}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </PageContent>

      <CreateTemplateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={createTemplate}
        creating={creating}
        language={i18n.language}
      />
    </>
  );
}
